import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { assetService, Asset, AssetType } from '@/firebase/assetService';
import { locationService, Location } from '@/firebase/locationService';
import { floorService, Floor } from '@/firebase/floorService';
import { checklistService, Checklist } from '@/firebase/checklistService';
import { formService, Form } from '@/firebase/formService';
import { checklistCompletionService, ChecklistCompletion } from '@/firebase/checklistCompletionService';
import { formCompletionService, FormCompletion } from '@/firebase/formCompletionService';
import { checklistRunService, ChecklistRun } from '@/firebase/checklistRunService';
import { checklistEvidenceService, ChecklistEvidence } from '@/firebase/checklistEvidenceService';
import { formatTimeDisplay } from '@/utils/checklistTimeTracker';
import { useAuth } from '@/contexts/AuthContext';
import { getTasksByAssetId, getTasksForOrganization, TaskWithDetails } from '@/services/taskService';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useNotification } from '@/contexts/NotificationContext';
import { validateItemCompletion, ItemValidationResult } from '@/services/checklistValidationService';
import { assetFactsService } from '@/firebase/assetFactsService';
import { enrichChecklistItem } from '@/services/checklistRequiredFieldsDefinitions';
import { QRCodeSVG } from 'qrcode.react';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import RunChecklist from './RunChecklist';
import RunForm from './RunForm';
import AssetFileUpload from './AssetFileUpload';
import { UserRolePicker } from './UserRolePicker';
import { 
  ArrowLeft, 
  Package, 
  MapPin, 
  Calendar, 
  CheckCircle2, 
  Clock,
  Download,
  Play,
  Printer,
  Image as ImageIcon,
  ClipboardList,
  FileText,
  Pencil,
  Save,
  X,
  AlertCircle,
  Eye,
  Paperclip,
  Plus
} from 'lucide-react';
import AddTaskPanel from './AddTaskPanel';
import CompleteTaskModal from './CompleteTaskModal';
import { completeTask } from '@/services/taskService';
import type { ComplianceTask } from '@/services/complianceTaskService';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { taskEvidenceDraftService } from '@/firebase/taskEvidenceDraftService';

function completionMatchesAsset(cc: ChecklistCompletion, targetAssetId: string): boolean {
  return (
    cc.assetId === targetAssetId ||
    (cc.variableValues?.assetId === targetAssetId) ||
    (cc.variableValues?.asset === targetAssetId)
  );
}

function getCompletionTimeMs(cc: ChecklistCompletion): number {
  const v = (cc as any).completedAt ?? (cc as any).completedDate;
  if (!v) return 0;
  const d = v?.toDate ? v.toDate() : new Date(v);
  return d.getTime();
}

function getTaskStartTimeMs(task: { createdAt?: any }): number {
  const v = task.createdAt;
  if (!v) return 0;
  const d = v?.toDate ? v.toDate() : new Date(v);
  return d.getTime();
}

const AssetRecordPage: React.FC = () => {
  const { assetId } = useParams<{ assetId: string }>();
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const { showNotification } = useNotification();
  const { user } = useAuth();
  
  const [asset, setAsset] = useState<Asset | null>(null);
  const [assetType, setAssetType] = useState<AssetType | null>(null);
  const [location, setLocation] = useState<Location | null>(null);
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [forms, setForms] = useState<Form[]>([]);
  const [inspectionHistory, setInspectionHistory] = useState<ChecklistCompletion[]>([]);
  const [formCompletions, setFormCompletions] = useState<FormCompletion[]>([]);
  const [tasks, setTasks] = useState<TaskWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedChecklist, setSelectedChecklist] = useState<Checklist | null>(null);
  const [selectedForm, setSelectedForm] = useState<Form | null>(null);
  const [isRunningChecklist, setIsRunningChecklist] = useState(false);
  const [isRunningForm, setIsRunningForm] = useState(false);
  const [selectedCompletion, setSelectedCompletion] = useState<ChecklistCompletion | FormCompletion | null>(null);
  const [selectedCompletionType, setSelectedCompletionType] = useState<'checklist' | 'form' | null>(null);
  const [viewingChecklist, setViewingChecklist] = useState<Checklist | null>(null);
  const [viewingForm, setViewingForm] = useState<Form | null>(null);
  const [isLoadingCompletionDetails, setIsLoadingCompletionDetails] = useState(false);
  const [activitiesDisplayLimit, setActivitiesDisplayLimit] = useState(10);
  const [editingFields, setEditingFields] = useState<Record<string, string>>({});
  const [isEditingFields, setIsEditingFields] = useState(false);
  const [isSavingFields, setIsSavingFields] = useState(false);
  const [pausedRuns, setPausedRuns] = useState<Record<string, ChecklistRun>>({}); // checklistId -> paused run
  const [checklistItemCounts, setChecklistItemCounts] = useState<Record<string, number>>({}); // checklistId -> total item count
  const [itemEvidenceMap, setItemEvidenceMap] = useState<Record<string, ChecklistEvidence[]>>({}); // itemText -> evidence[]
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set()); // Set of expanded item texts
  const [assetFacts, setAssetFacts] = useState<Record<string, any>>({}); // Asset facts for validation
  const [itemValidationResults, setItemValidationResults] = useState<Record<string, ItemValidationResult>>({}); // itemText -> validation result
  const [showAddTaskPanel, setShowAddTaskPanel] = useState(false);
  const [taggedFloor, setTaggedFloor] = useState<Floor | null>(null);
  const [viewingTask, setViewingTask] = useState<TaskWithDetails | null>(null);
  const [taskDraftEvidenceReady, setTaskDraftEvidenceReady] = useState<Record<string, boolean>>({});
  const { subscription } = useSubscription();
  const canCreateTasks = subscription?.plan && !['free', 'communicate'].includes((subscription.plan as string).toLowerCase());

  // Open tasks for display: all tasks with pending/overdue/pending_review (section shows all, including those with evidence).
  const openTasksForAsset = useMemo(
    () => tasks.filter((t) => ['pending', 'overdue', 'pending_review'].includes(t.status)),
    [tasks]
  );
  // Helper: tasks that need action (exclude those with checklist evidence for this asset).
  const openTasksNeedingAction = useMemo(() => {
    if (!assetId) return [];
    const checklistTemplateMap = new Map<string, string>();
    checklists.forEach((c) => {
      if (c.id && c.templateId) checklistTemplateMap.set(c.id, c.templateId);
    });
    return openTasksForAsset.filter((task) => {
      const taskStartMs = getTaskStartTimeMs(task);
      const hasChecklistEvidence = inspectionHistory.some(
        (cc) =>
          cc.checklistId &&
          checklistTemplateMap.get(cc.checklistId) === task.templateId &&
          completionMatchesAsset(cc, assetId) &&
          getCompletionTimeMs(cc) >= taskStartMs // Completion must be on or after task start
      );
      return !hasChecklistEvidence; // Only count as open if evidence NOT available
    });
  }, [openTasksForAsset, inspectionHistory, checklists, assetId]);

  const openTaskCount = openTasksNeedingAction.length;

  // Split open tasks into assigned vs compliance (for header display).
  const assignedOpenCount = useMemo(() => {
    return openTasksNeedingAction.filter((t) => t.assetIds?.includes(assetId || '')).length;
  }, [openTasksNeedingAction, assetId]);

  const complianceOpenCount = useMemo(() => {
    const templateIds = assetType?.complianceTemplateIds ?? [];
    return openTasksNeedingAction.filter(
      (t) => templateIds.includes(t.templateId) && !t.assetIds?.includes(assetId || '')
    ).length;
  }, [openTasksNeedingAction, assetType?.complianceTemplateIds, assetId]);

  // Helper function to load checklist item counts
  const loadChecklistItemCounts = async (completions: ChecklistCompletion[]) => {
    const checklistIds = new Set(
      completions
        .map(c => c.checklistId)
        .filter((id): id is string => !!id)
    );
    
    if (checklistIds.size === 0) {
      return; // No checklist IDs to load
    }
    
    // Check current state to see which IDs we need to load
    setChecklistItemCounts(prev => {
      // Filter out checklist IDs we already have counts for
      const idsToLoad = Array.from(checklistIds).filter(id => !prev[id]);
      
      if (idsToLoad.length === 0) {
        return prev; // All counts already loaded
      }
      
      // Load checklists in parallel to get item counts (async, outside of setState)
      Promise.all(
        idsToLoad.map(async (checklistId) => {
          try {
            console.log(`[AssetRecordPage] Loading checklist ${checklistId} for item count...`);
            const result = await checklistService.getChecklist(checklistId);
            if (result.success && result.data) {
              const itemCount = result.data.items?.length || 0;
              console.log(`[AssetRecordPage] Loaded checklist ${checklistId}: ${itemCount} items`);
              return { checklistId, itemCount };
            }
            return null;
          } catch (error) {
            console.error(`[AssetRecordPage] Error loading checklist ${checklistId}:`, error);
            return null;
          }
        })
      ).then(results => {
        const counts: Record<string, number> = {};
        results.forEach(result => {
          if (result) {
            counts[result.checklistId] = result.itemCount;
          }
        });
        
        if (Object.keys(counts).length > 0) {
          console.log(`[AssetRecordPage] Updating checklist item counts:`, counts);
          setChecklistItemCounts(current => ({ ...current, ...counts }));
        }
      });
      
      return prev; // Return immediately, update will happen async
    });
  };
  
  // Generate QR code URL
  const qrCodeUrl = assetId 
    ? `${window.location.origin}/asset/${assetId}`
    : '';

  useEffect(() => {
    if (assetId && currentOrganization?.id) {
      loadAssetData();
    }
  }, [assetId, currentOrganization?.id]);

  // Listen for auto-completion events to refresh completion data
  useEffect(() => {
    const handleAutoComplete = (event: CustomEvent) => {
      if (event.detail?.assetId === assetId) {
        // Reload completion data after auto-completion
        loadAssetData();
      }
    };

    window.addEventListener('checklistAutoCompleted', handleAutoComplete as EventListener);
    return () => {
      window.removeEventListener('checklistAutoCompleted', handleAutoComplete as EventListener);
    };
  }, [assetId]);

  // Load checklist item counts when inspection history changes
  useEffect(() => {
    if (inspectionHistory.length > 0) {
      loadChecklistItemCounts(inspectionHistory);
    }
  }, [inspectionHistory]);

  const loadChecklistsAndForms = async (templateIds: string[], assetLocationId?: string) => {
    if (!currentOrganization?.id) return;
    
    try {
      // Load all checklists and forms for the organization
      const [checklistsResult, formsResult] = await Promise.all([
        checklistService.getAllChecklists(currentOrganization.id),
        formService.getAllForms(currentOrganization.id)
      ]);

      // Filter checklists that match any of the template IDs and location (if specified)
      if (checklistsResult.success && checklistsResult.data) {
        const matchingChecklists = checklistsResult.data.filter(checklist => {
          // Must match one of the template IDs
          if (!checklist.templateId || !templateIds.includes(checklist.templateId)) {
            return false;
          }
          
          // If checklist has locationIds specified, must match asset's location
          if (checklist.locationIds && checklist.locationIds.length > 0) {
            const locationToMatch = assetLocationId || asset?.locationId;
            if (!locationToMatch || !checklist.locationIds.includes(locationToMatch)) {
              console.log(`[AssetRecordPage] Checklist "${checklist.name}" excluded:`, {
                checklistLocationIds: checklist.locationIds,
                assetLocationId: locationToMatch,
                matches: checklist.locationIds.includes(locationToMatch || '')
              });
              return false;
            }
            console.log(`[AssetRecordPage] Checklist "${checklist.name}" included:`, {
              checklistLocationIds: checklist.locationIds,
              assetLocationId: locationToMatch
            });
          }
          
          // If no locationIds specified, available for all locations (backward compatible)
          return true;
        });
        console.log(`[AssetRecordPage] Filtered ${matchingChecklists.length} checklists from ${checklistsResult.data.length} total`, {
          templateIds,
          assetLocationId: assetLocationId || asset?.locationId,
          matchingChecklists: matchingChecklists.map(c => c.name)
        });
        setChecklists(matchingChecklists);
      }

      // Filter forms that match any of the template IDs AND location (if specified)
      if (formsResult.success && formsResult.data) {
        const matchingForms = formsResult.data.filter(form => {
          // Must match one of the template IDs
          if (!form.templateId || !templateIds.includes(form.templateId)) {
            return false;
          }
          
          // If form has locationIds specified, must match asset's location
          if (form.locationIds && form.locationIds.length > 0) {
            const locationToMatch = assetLocationId || asset?.locationId;
            if (!locationToMatch || !form.locationIds.includes(locationToMatch)) {
              console.log(`[AssetRecordPage] Form "${form.name}" excluded:`, {
                formLocationIds: form.locationIds,
                assetLocationId: locationToMatch,
                matches: form.locationIds.includes(locationToMatch || '')
              });
              return false;
            }
            console.log(`[AssetRecordPage] Form "${form.name}" included:`, {
              formLocationIds: form.locationIds,
              assetLocationId: locationToMatch
            });
          }
          
          // If no locationIds specified, available for all locations (backward compatible)
          return true;
        });
        console.log(`[AssetRecordPage] Filtered ${matchingForms.length} forms from ${formsResult.data.length} total`, {
          templateIds,
          assetLocationId: assetLocationId || asset?.locationId,
          matchingForms: matchingForms.map(f => f.name)
        });
        setForms(matchingForms);
      }
    } catch (error: any) {
      console.error('Error loading checklists and forms:', error);
    }
  };

  const loadAssetData = async () => {
    if (!assetId || !currentOrganization?.id) return;
    
    setIsLoading(true);
    try {
      // Load asset
      const assetResult = await assetService.getAsset(assetId);
      if (!assetResult.success || !assetResult.data) {
        showNotification('Asset not found', 'error');
        navigate('/assets');
        return;
      }
      setAsset(assetResult.data);

      // Load asset type
      const assetTypeResult = await assetService.getAssetType(assetResult.data.assetTypeId);
      if (assetTypeResult.success && assetTypeResult.data) {
        setAssetType(assetTypeResult.data);
        
        // Load checklists and forms associated with this asset type's templates
        if (assetTypeResult.data.complianceTemplateIds && assetTypeResult.data.complianceTemplateIds.length > 0) {
          await loadChecklistsAndForms(assetTypeResult.data.complianceTemplateIds, assetResult.data.locationId);
        }
      }

      // Load location
      const locationResult = await locationService.getLocation(assetResult.data.locationId);
      if (locationResult.success && locationResult.data) {
        setLocation(locationResult.data);
      }

      // Load floor name if asset is tagged on floor map
      if (assetResult.data.floorId) {
        const floorResult = await floorService.getFloor(assetResult.data.floorId);
        setTaggedFloor(floorResult.success && floorResult.data ? floorResult.data : null);
      } else {
        setTaggedFloor(null);
      }

      // Load inspection history - get checklist and form completions for this asset
      // Load tasks: (1) tagged to this asset, (2) Option C compliance tasks (template applies via assetType)
      const templateIds = assetTypeResult.success && assetTypeResult.data?.complianceTemplateIds
        ? assetTypeResult.data.complianceTemplateIds
        : [];
      const [checklistHistoryResult, formHistoryResult, tasksByAsset, orgTasks] = await Promise.all([
        checklistCompletionService.getCompletionsForAsset(assetId),
        formCompletionService.getCompletionsForAsset(assetId),
        getTasksByAssetId(assetId),
        templateIds.length > 0 ? getTasksForOrganization(currentOrganization.id) : Promise.resolve([])
      ]);

      if (checklistHistoryResult.success && checklistHistoryResult.data) {
        setInspectionHistory(checklistHistoryResult.data);
        loadChecklistItemCounts(checklistHistoryResult.data);
      }

      if (formHistoryResult.success && formHistoryResult.data) {
        setFormCompletions(formHistoryResult.data);
      }

      const taggedTasks = tasksByAsset || [];
      const complianceTasks = (orgTasks || []).filter(
        (t) => t.locationId === assetResult.data.locationId &&
          templateIds.includes(t.templateId) &&
          ['pending', 'overdue', 'pending_review'].includes(t.status)
      );
      const taskIds = new Set(taggedTasks.map((t) => t.id));
      const merged = [...taggedTasks];
      for (const t of complianceTasks) {
        if (!taskIds.has(t.id)) {
          taskIds.add(t.id);
          merged.push(t);
        }
      }
      setTasks(merged);
      
      // Load draft evidence status for Required Evidence section
      loadTaskDraftEvidenceStatus(merged, assetId);

      // Load paused runs for this asset if user is logged in
      if (user?.id && assetId) {
        loadPausedRuns(assetId);
      }
    } catch (error: any) {
      console.error('Error loading asset data:', error);
      showNotification(`Error loading asset: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadPausedRuns = async (assetId: string) => {
    if (!user?.id) return;
    
    try {
      // Get all active runs for the user
      const runsResult = await checklistRunService.getActiveRunsForUser(user.id);
      if (runsResult.success && runsResult.data) {
        // Filter for paused runs that match this asset
        const assetPausedRuns: Record<string, ChecklistRun> = {};
        runsResult.data.forEach((run) => {
          // Check if this run is paused and matches the asset
          if (run.status === 'paused' && 
              (run.assetId === assetId || run.variableValues?.['assetId'] === assetId)) {
            // Store by checklistId
            if (run.checklistId) {
              assetPausedRuns[run.checklistId] = run;
            }
          }
        });
        setPausedRuns(assetPausedRuns);
      }
    } catch (error) {
      console.error('Error loading paused runs:', error);
    }
  };

  /** Load draft evidence status for open tasks - determines if required evidence is already uploaded */
  const loadTaskDraftEvidenceStatus = async (taskList: TaskWithDetails[], targetAssetId: string) => {
    if (!targetAssetId || taskList.length === 0) {
      setTaskDraftEvidenceReady({});
      return;
    }
    const getEvidenceKey = (e: { type?: string; label?: string }, idx: number) =>
      `${e.type || 'file'}_${idx}`;
    const ready: Record<string, boolean> = {};
    await Promise.all(
      taskList.map(async (task) => {
        const requiredEvidence = (task.evidenceTypes || []).filter(
          (e: { required?: boolean }) => e.required === true || e.required === undefined
        );
        if (requiredEvidence.length === 0) {
          ready[task.id] = true;
          return;
        }
        const draftResult = await taskEvidenceDraftService.getDraft(task.id);
        if (!draftResult.success || !draftResult.data?.assetEvidence) {
          ready[task.id] = false;
          return;
        }
        const assetEvidence = draftResult.data.assetEvidence[targetAssetId] || {};
        const allSatisfied = requiredEvidence.every((ev, idx) => {
          const key = getEvidenceKey(ev as any, idx);
          return !!assetEvidence[key];
        });
        ready[task.id] = allSatisfied;
      })
    );
    setTaskDraftEvidenceReady(ready);
  };

  const handleDownloadQR = async () => {
    if (!asset || !assetId) return;
    
    try {
      // Generate PDF with QR code, logo, and asset name
      // Total size: 2" x 2", QR code: 1.5" x 1.5"
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'in',
        format: [2, 2] // 2" x 2" square
      });

      const qrUrl = `${window.location.origin}/asset/${assetId}`;
      const qrSize = 1.5; // QR code size
      const totalSize = 2; // Total PDF size
      const centerX = totalSize / 2;
      const qrX = centerX - qrSize / 2; // Center the QR code horizontally
      
      // Generate QR code as data URL
      const qrDataUrl = await QRCode.toDataURL(qrUrl, {
        width: 288, // High resolution
        margin: 2,
        errorCorrectionLevel: 'H'
      });

      // Position elements vertically in 2" space
      const logoY = 0.15; // Logo at top
      const qrY = 0.35; // QR code below logo
      const assetNameY = 1.85; // Asset name at bottom
      
      // Calculate logo dimensions to center it
      const circleRadius = 0.05;
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      const textWidth = pdf.getTextWidth('complin');
      const gap = 0.03;
      const totalLogoWidth = circleRadius * 2 + gap + textWidth;
      const logoStartX = centerX - totalLogoWidth / 2;
      
      // Add logo (black circle) - centered
      pdf.setFillColor(0, 0, 0);
      pdf.circle(logoStartX + circleRadius, logoY, circleRadius, 'F');
      
      // Add logo text - centered
      pdf.setTextColor(0, 0, 0);
      pdf.text('complin', logoStartX + circleRadius * 2 + gap, logoY + 0.03, { align: 'left' });
      
      // Add QR code (1.5" x 1.5") centered
      pdf.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
      
      // Add asset name below QR code - centered
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      const assetNameText = asset.assetName.length > 20 
        ? asset.assetName.substring(0, 20) + '...' 
        : asset.assetName;
      pdf.text(assetNameText.toLowerCase(), centerX, assetNameY, {
        align: 'center',
        maxWidth: qrSize - 0.1
      });

      // Save PDF
      const fileName = `asset-qr-${assetId}.pdf`;
      pdf.save(fileName);
    } catch (error: any) {
      console.error('Error generating QR PDF:', error);
      showNotification(`Error generating QR code: ${error.message}`, 'error');
    }
  };

  const handlePrintQR = () => {
    if (!asset) return;
    
    // Create a new window with QR code, logo, and asset name
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const qrCodeSvg = document.getElementById('qr-code');
    if (!qrCodeSvg) return;

    // Get the SVG as string
    const svgString = new XMLSerializer().serializeToString(qrCodeSvg);

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>QR Code - ${asset.assetName}</title>
          <style>
            @page {
              size: 1.5in 1.5in;
              margin: 0;
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              width: 1.5in;
              height: 1.5in;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 0.1in;
              font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            }
            .logo-container {
              display: flex;
              align-items: center;
              gap: 4px;
              margin-bottom: 4px;
            }
            .logo-circle {
              width: 12px;
              height: 12px;
              border-radius: 50%;
              background-color: #000000;
              flex-shrink: 0;
            }
            .logo-text {
              font-size: 10px;
              font-weight: 700;
              color: #000000;
              letter-spacing: -0.5px;
              text-transform: lowercase;
            }
            .qr-code-container {
              display: flex;
              justify-content: center;
              align-items: center;
              margin: 2px 0;
            }
            .qr-code-container svg {
              width: 1.1in;
              height: 1.1in;
            }
            .asset-name {
              font-size: 8px;
              font-weight: 500;
              color: #000000;
              text-align: center;
              text-transform: lowercase;
              margin-top: 2px;
              max-width: 100%;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <div class="logo-container">
            <div class="logo-circle"></div>
            <span class="logo-text">complin</span>
          </div>
          <div class="qr-code-container">
            ${svgString}
          </div>
          <div class="asset-name">${asset.assetName}</div>
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const handleRunChecklist = (checklist: Checklist) => {
    setSelectedChecklist(checklist);
    setIsRunningChecklist(true);
  };

  const handleRunForm = (form: Form) => {
    setSelectedForm(form);
    setIsRunningForm(true);
  };

  const handleChecklistComplete = async () => {
    setIsRunningChecklist(false);
    setSelectedChecklist(null);
    // Reload inspection history for this asset
    if (assetId) {
      const [checklistHistoryResult, formHistoryResult] = await Promise.all([
        checklistCompletionService.getCompletionsForAsset(assetId),
        formCompletionService.getCompletionsForAsset(assetId)
      ]);
      
      if (checklistHistoryResult.success && checklistHistoryResult.data) {
        setInspectionHistory(checklistHistoryResult.data);
        // Load checklist item counts for accurate display
        loadChecklistItemCounts(checklistHistoryResult.data);
      }
      
      if (formHistoryResult.success && formHistoryResult.data) {
        setFormCompletions(formHistoryResult.data);
      }
      
      // Reload paused runs to update banners
      if (user?.id) {
        loadPausedRuns(assetId);
      }
    }
  };

  const handleFormComplete = async () => {
    setIsRunningForm(false);
    setSelectedForm(null);
    // Reload inspection history for this asset
    if (assetId) {
      const [checklistHistoryResult, formHistoryResult] = await Promise.all([
        checklistCompletionService.getCompletionsForAsset(assetId),
        formCompletionService.getCompletionsForAsset(assetId)
      ]);
      
      if (checklistHistoryResult.success && checklistHistoryResult.data) {
        setInspectionHistory(checklistHistoryResult.data);
        // Load checklist item counts for accurate display
        loadChecklistItemCounts(checklistHistoryResult.data);
      }
      
      if (formHistoryResult.success && formHistoryResult.data) {
        setFormCompletions(formHistoryResult.data);
      }
    }
  };

  const handleStartEditFields = () => {
    // Initialize editing fields with current values
    const currentValues = asset?.fieldValues || {};
    setEditingFields({ ...currentValues });
    setIsEditingFields(true);
  };

  const handleCancelEditFields = () => {
    setEditingFields({});
    setIsEditingFields(false);
  };

  const handleSaveFields = async () => {
    if (!asset?.id) return;
    
    setIsSavingFields(true);
    try {
      // Clean fieldValues - only include non-empty values
      const cleanedFieldValues: Record<string, string> = {};
      if (assetType?.customFields && assetType.customFields.length > 0) {
        assetType.customFields.forEach(field => {
          const value = editingFields[field.name]?.trim();
          if (value) {
            cleanedFieldValues[field.name] = value;
          }
        });
      }
      
      const userInfo = user ? { id: user.id, name: user.name || 'Unknown', email: user.email || 'Unknown' } : undefined;
      const updateResult = await assetService.updateAsset(asset.id, {
        fieldValues: Object.keys(cleanedFieldValues).length > 0 ? cleanedFieldValues : undefined
      }, userInfo, currentOrganization?.id);
      
      if (updateResult.success) {
        showNotification('Custom fields updated successfully', 'success');
        setIsEditingFields(false);
        // Reload asset data to show updated values
        await loadAssetData();
      } else {
        showNotification(`Error updating fields: ${updateResult.error}`, 'error');
      }
    } catch (error: any) {
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setIsSavingFields(false);
    }
  };

  const handleFieldValueChange = (fieldName: string, value: string) => {
    setEditingFields({ ...editingFields, [fieldName]: value });
  };

  const handleViewCompletion = async (completion: ChecklistCompletion | FormCompletion, type: 'checklist' | 'form') => {
    setSelectedCompletion(completion);
    setSelectedCompletionType(type);
    setItemEvidenceMap({});
    setExpandedItems(new Set());
    setItemValidationResults({});
    setIsLoadingCompletionDetails(true);

    try {
      if (type === 'checklist') {
        // Reload completion from Firestore to get latest itemResults
        let checklistCompletion = completion as ChecklistCompletion;
        if (completion.id) {
          const freshCompletionResult = await checklistCompletionService.getCompletion(completion.id);
          if (freshCompletionResult.success && freshCompletionResult.data) {
            checklistCompletion = freshCompletionResult.data;
            setSelectedCompletion(checklistCompletion); // Update with fresh data
          }
        }
        const checklistResult = await checklistService.getChecklist(checklistCompletion.checklistId);
        if (checklistResult.success && checklistResult.data) {
          // Use checklist as-is - don't enrich when viewing (show only fields actually defined in the checklist)
          setViewingChecklist(checklistResult.data);
          
          // Load asset facts for validation
          let factsMap: Record<string, any> = {};
          if (checklistCompletion.assetId) {
            const factsResult = await assetFactsService.getAssetFacts(checklistCompletion.assetId);
            if (factsResult.success && factsResult.data) {
              Object.entries(factsResult.data.facts || {}).forEach(([key, fact]) => {
                factsMap[key] = fact;
              });
              setAssetFacts(factsMap);
            }
          }
          
          // Load evidence for all items and validate
          if (completion.id) {
            const evidenceMap: Record<string, ChecklistEvidence[]> = {};
            const validationResults: Record<string, ItemValidationResult> = {};
            
            const evidencePromises = (checklistResult.data.items || []).map(async (item) => {
              try {
                const evidenceResult = await checklistEvidenceService.getEvidenceByItemResult(
                  completion.id!,
                  item.text
                );
                if (evidenceResult.success && evidenceResult.data) {
                  evidenceMap[item.text] = evidenceResult.data;
                  
                  // Extract field values from evidence notes
                  const evidenceFields: Record<string, any> = {};
                  evidenceResult.data.forEach(ev => {
                    if (ev.notes && ev.notes.includes('Auto-extracted fields:')) {
                      try {
                        const fieldsMatch = ev.notes.match(/Auto-extracted fields: (.+)/);
                        if (fieldsMatch) {
                          const fields = JSON.parse(fieldsMatch[1]);
                          // Only assign non-empty values
                          Object.entries(fields).forEach(([key, value]) => {
                            if (value !== null && value !== undefined && value !== '') {
                              evidenceFields[key] = value;
                            }
                          });
                        }
                      } catch (error) {
                        console.error('Error parsing evidence fields:', error);
                      }
                    }
                  });
                  
                  // Validate item
                  const itemResult = checklistCompletion.itemResults?.[item.text];
                  const validation = validateItemCompletion(
                    item,
                    itemResult,
                    factsMap,
                    evidenceFields
                  );
                  validationResults[item.text] = validation;
                } else {
                  evidenceMap[item.text] = [];
                  
                  // Still validate even without evidence (enrich temporarily for validation only)
                  const enrichedItem = enrichChecklistItem({ ...item });
                  const itemResult = checklistCompletion.itemResults?.[item.text];
                  const validation = validateItemCompletion(
                    enrichedItem,
                    itemResult,
                    factsMap,
                    {}
                  );
                  validationResults[item.text] = validation;
                }
              } catch (error) {
                console.error(`Error loading evidence for item "${item.text}":`, error);
                evidenceMap[item.text] = [];
              }
            });
            
            await Promise.all(evidencePromises);
            setItemEvidenceMap(evidenceMap);
            setItemValidationResults(validationResults);
            
            // Expand all items by default
            if (checklistResult.data.items) {
              const allItemTexts = new Set(checklistResult.data.items.map(item => item.text));
              setExpandedItems(allItemTexts);
            }
          }
        }
      } else {
        const formResult = await formService.getForm((completion as FormCompletion).formId);
        if (formResult.success && formResult.data) {
          setViewingForm(formResult.data);
        }
      }
    } catch (error: any) {
      console.error('Error loading completion details:', error);
      showNotification(`Error loading details: ${error.message}`, 'error');
    } finally {
      setIsLoadingCompletionDetails(false);
    }
  };

  const handleCloseCompletionDetails = () => {
    setSelectedCompletion(null);
    setSelectedCompletionType(null);
    setItemEvidenceMap({});
    setExpandedItems(new Set());
    setItemValidationResults({});
    setAssetFacts({});
    setViewingChecklist(null);
    setViewingForm(null);
  };

  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    try {
      const d = date.toDate ? date.toDate() : new Date(date);
      // Check if date is valid
      if (isNaN(d.getTime())) {
        return 'Invalid date';
      }
      return d.toLocaleString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading asset details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!asset || !assetType) {
    return (
      <div className="p-4 max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="text-center py-12">
            <Package className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <p className="text-xl font-semibold text-gray-700">Asset not found</p>
            <Link to="/assets" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">
              Return to Asset Management
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/assets')}
              className="text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{asset.assetName}</h1>
              <p className="text-gray-600 mt-1">{assetType.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canCreateTasks && asset?.locationId && (
              <button
                onClick={() => setShowAddTaskPanel(true)}
                className="btn-primary px-4 py-2 flex items-center gap-2"
                title="Create a task for this asset"
              >
                <Plus className="w-4 h-4" />
                Add Task
              </button>
            )}
            <button
              onClick={handleDownloadQR}
              className="btn-secondary px-4 py-2 flex items-center gap-2"
              title="Download QR Code"
            >
              <Download className="w-4 h-4" />
              Download QR
            </button>
            <button
              onClick={handlePrintQR}
              className="btn-secondary px-4 py-2 flex items-center gap-2"
              title="Print QR Code"
            >
              <Printer className="w-4 h-4" />
              Print QR
            </button>
          </div>
        </div>

        {/* Quick Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          {openTaskCount > 0 && (
            <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <ClipboardList className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 mb-1">Open tasks</p>
                <div className="text-sm font-semibold text-amber-900 space-y-0.5">
                  <p>
                    <a
                      href={`/tasks?assetId=${encodeURIComponent(assetId || '')}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/tasks?assetId=${encodeURIComponent(assetId || '')}`);
                      }}
                      className="text-primary-600 hover:text-primary-700 hover:underline"
                    >
                      Tasks assigned to assets: {assignedOpenCount}
                    </a>
                  </p>
                  <p>
                    <a
                      href="#required-evidence"
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById('required-evidence')?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="text-primary-600 hover:text-primary-700 hover:underline"
                    >
                      Compliance tasks: {complianceOpenCount}
                    </a>
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <MapPin className="w-5 h-5 text-primary-600" />
            <div>
              <p className="text-xs text-gray-500">Location</p>
              <p className="text-sm font-semibold text-gray-900">
                {location?.name || 'Unknown'}
              </p>
            </div>
          </div>
          
          {asset.assetLocation && (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Package className="w-5 h-5 text-primary-600" />
              <div>
                <p className="text-xs text-gray-500">Asset Location</p>
                <p className="text-sm font-semibold text-gray-900">{asset.assetLocation}</p>
              </div>
            </div>
          )}

          {asset.floorId && taggedFloor && asset.locationId && (
            <a
              href={`/floor-map/full?locationId=${asset.locationId}&floorId=${asset.floorId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200 hover:bg-green-100 hover:border-green-300 cursor-pointer transition-colors"
            >
              <MapPin className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-xs text-gray-500">Tagged on Floor Map</p>
                <p className="text-sm font-semibold text-green-800">{taggedFloor.name}</p>
              </div>
            </a>
          )}

          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <Calendar className="w-5 h-5 text-primary-600" />
            <div>
              <p className="text-xs text-gray-500">Asset ID</p>
              <p className="text-sm font-semibold text-gray-900 font-mono">{assetId}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Photo Section */}
          {asset.photoUrl && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-primary-600" />
                Asset Photo
              </h2>
              <img
                src={asset.photoUrl}
                alt={asset.assetName}
                className="w-full rounded-lg border border-gray-200"
              />
            </div>
          )}

          {/* File Upload Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Paperclip className="w-5 h-5 text-primary-600" />
              Asset Files
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Upload documents and files for this asset.
            </p>
            <AssetFileUpload 
              assetId={assetId!}
              onFileUploaded={(file) => {
                console.log('File uploaded:', file);
                showNotification('File uploaded successfully', 'success');
              }}
              onFileDeleted={(fileId) => {
                console.log('File deleted:', fileId);
                showNotification('File deleted successfully', 'success');
              }}
            />
          </div>

          {/* Available Checklists and Forms */}
          {(checklists.length > 0 || forms.length > 0) && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Available Checklists & Forms
              </h2>
              
              {/* Checklists Section */}
              {checklists.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <ClipboardList className="w-4 h-4" />
                    Checklists
                  </h3>
                  <div className="space-y-3">
                    {checklists.map((checklist) => (
                      <div
                        key={checklist.id}
                        className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{checklist.name}</h4>
                            {checklist.description && (
                              <p className="text-sm text-gray-600 mt-1">{checklist.description}</p>
                            )}
                            {checklist.items && (
                              <p className="text-xs text-gray-500 mt-1">
                                {checklist.items.length} item{checklist.items.length !== 1 ? 's' : ''}
                              </p>
                            )}
                            {/* Paused Checklist Banner */}
                            {checklist.id && pausedRuns[checklist.id] && (
                              <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded px-2 py-1 text-xs text-yellow-800">
                                Will resume paused checklist
                              </div>
                            )}
                          </div>
                          <button 
                            onClick={() => handleRunChecklist(checklist)}
                            className="btn-primary px-4 py-2 flex items-center gap-2 whitespace-nowrap ml-4"
                          >
                            <Play className="w-4 h-4" />
                            Run Checklist
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Forms Section */}
              {forms.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Forms
                  </h3>
                  <div className="space-y-3">
                    {forms.map((form) => (
                      <div
                        key={form.id}
                        className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-gray-900">{form.name}</h4>
                            {form.description && (
                              <p className="text-sm text-gray-600 mt-1">{form.description}</p>
                            )}
                            {form.fields && (
                              <p className="text-xs text-gray-500 mt-1">
                                {form.fields.length} field{form.fields.length !== 1 ? 's' : ''}
                              </p>
                            )}
                          </div>
                          <button 
                            onClick={() => handleRunForm(form)}
                            className="btn-primary px-4 py-2 flex items-center gap-2 whitespace-nowrap"
                          >
                            <Play className="w-4 h-4" />
                            Run Form
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* No Checklists/Forms Message */}
          {checklists.length === 0 && forms.length === 0 && assetType && assetType.complianceTemplateIds && assetType.complianceTemplateIds.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <p className="text-sm text-yellow-800">
                <strong>No checklists or forms available yet.</strong> Create checklists or forms associated with this asset type's compliance templates to run inspections directly from this page.
              </p>
            </div>
          )}

          {/* Required Evidence & Pending Compliance Tasks – only tasks without evidence from completed checklist */}
          {(() => {
            const tasksWithRequiredEvidence = openTasksForAsset.filter(
              (t) => t.evidenceTypes?.length > 0
            );
            if (openTasksForAsset.length === 0) return null;

            // Map checklistId -> templateId for matching completions to tasks
            const checklistTemplateMap = new Map<string, string>();
            checklists.forEach((c) => {
              if (c.id && c.templateId) checklistTemplateMap.set(c.id, c.templateId);
            });

            return (
              <div id="required-evidence" className="bg-white rounded-lg shadow-md p-6 scroll-mt-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  Required Evidence & Pending Compliance
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  {openTasksForAsset.length} open task{openTasksForAsset.length > 1 ? 's' : ''} apply to this asset.
                  {tasksWithRequiredEvidence.length > 0 && (
                    <> Upload required evidence or run checklists above.</>
                  )}
                </p>
                <div className="space-y-3">
                  {openTasksForAsset.map((task) => {
                    const requiredEvidence = (task.evidenceTypes || []).filter(
                      (e: { required?: boolean }) => e.required === true || e.required === undefined
                    );
                    const hasDraftEvidence = taskDraftEvidenceReady[task.id] === true;

                    // Last related checklist completed for this compliance (same templateId) – evidence from this asset only, completed on or after task start
                    const taskStartMs = getTaskStartTimeMs(task);
                    const relatedCompletions = inspectionHistory
                      .filter(
                        (cc) =>
                          cc.checklistId &&
                          checklistTemplateMap.get(cc.checklistId) === task.templateId &&
                          assetId &&
                          completionMatchesAsset(cc, assetId) &&
                          getCompletionTimeMs(cc) >= taskStartMs
                      )
                      .sort((a, b) => getCompletionTimeMs(b) - getCompletionTimeMs(a));
                    const lastRelatedCompletion = relatedCompletions[0];
                    const hasChecklistEvidence = !!lastRelatedCompletion;

                    return (
                      <div
                        key={task.id}
                        className={`p-4 border rounded-lg hover:border-primary-300 ${
                          hasChecklistEvidence ? 'border-green-300 bg-green-50/50' :
                          hasDraftEvidence ? 'border-green-300 bg-green-50/50' : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h4 className="font-medium text-gray-900">{task.templateName}</h4>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              hasChecklistEvidence ? 'bg-green-100 text-green-700' :
                              task.status === 'overdue' ? 'bg-red-100 text-red-700' :
                              task.status === 'pending_review' ? 'bg-amber-100 text-amber-700' :
                              'bg-gray-200 text-gray-700'
                            }`}>
                              {hasChecklistEvidence ? 'Evidence available' : task.status}
                            </span>
                            <p className="text-[11px] text-gray-500 mt-1">
                              Task: {task.templateName} • Start: {formatDate(task.createdAt)} • Due: {formatDate(task.dueDate)}
                            </p>
                            {lastRelatedCompletion && (
                              <p className="text-xs mt-1.5 flex items-center gap-1.5">
                                <ClipboardList className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                <button
                                  type="button"
                                  onClick={() => handleViewCompletion(lastRelatedCompletion, 'checklist')}
                                  className="text-primary-600 hover:text-primary-700 hover:underline font-medium text-left"
                                >
                                  {lastRelatedCompletion.checklistName || 'Checklist'} – {formatDate(lastRelatedCompletion.completedAt)}
                                </button>
                              </p>
                            )}
                            {!hasChecklistEvidence && requiredEvidence.length > 0 && (
                              <p className="text-sm text-gray-600 mt-2 flex items-center gap-2">
                                {hasDraftEvidence ? (
                                  <>
                                    <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                                    <span className="text-green-700">Evidence uploaded – complete task to submit</span>
                                  </>
                                ) : (
                                  <>Required evidence: {requiredEvidence.map((e: { label?: string; type?: string }) => e.label || e.type || 'Evidence').join(', ')}</>
                                )}
                              </p>
                            )}
                            {hasChecklistEvidence && (
                              <p className="text-sm text-green-700 mt-2 flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 shrink-0" />
                                Evidence from completed checklist above – view to see details
                              </p>
                            )}
                          </div>
                          {hasChecklistEvidence ? (
                            <button
                              type="button"
                              onClick={() => handleViewCompletion(lastRelatedCompletion!, 'checklist')}
                              className="btn-secondary px-3 py-2 text-sm whitespace-nowrap flex items-center gap-1.5"
                            >
                              <ClipboardList className="w-4 h-4" />
                              View Checklist
                            </button>
                          ) : (
                            <button
                              onClick={() => setViewingTask(task)}
                              className="btn-primary px-3 py-2 text-sm whitespace-nowrap flex items-center gap-1.5"
                            >
                              <Paperclip className="w-4 h-4" />
                              Upload Evidence
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Inspection History */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary-600" />
              Past Activities
            </h2>
            {inspectionHistory.length === 0 && formCompletions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle2 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-sm">No activities recorded yet</p>
                <p className="text-xs text-gray-400 mt-1">Start an inspection using the checklists or forms above</p>
              </div>
            ) : (() => {
              // Merge and sort all activities by date (most recent first)
              // Helper function to parse date safely
              const parseDate = (dateValue: any): Date | null => {
                if (!dateValue) return null;
                try {
                  let parsedDate: Date;
                  if (dateValue.toDate && typeof dateValue.toDate === 'function') {
                    parsedDate = dateValue.toDate();
                  } else if (typeof dateValue === 'number') {
                    parsedDate = new Date(dateValue);
                  } else if (dateValue instanceof Date) {
                    parsedDate = dateValue;
                  } else {
                    parsedDate = new Date(dateValue);
                  }
                  // Return null if date is invalid
                  return isNaN(parsedDate.getTime()) ? null : parsedDate;
                } catch {
                  return null;
                }
              };

              const allActivities: Array<{
                id: string;
                type: 'checklist' | 'form';
                data: ChecklistCompletion | FormCompletion;
                date: Date;
              }> = [
                ...inspectionHistory.map(comp => ({
                  id: comp.id || '',
                  type: 'checklist' as const,
                  data: comp,
                  date: parseDate(comp.completedAt) || new Date(0) // Use epoch as fallback for invalid dates (will sort to bottom)
                })),
                ...formCompletions.map(comp => ({
                  id: comp.id || '',
                  type: 'form' as const,
                  data: comp,
                  date: parseDate(comp.completionDate) || new Date(0) // Use epoch as fallback for invalid dates (will sort to bottom)
                }))
              ]
              // Filter out entries with invalid dates (epoch date means it was invalid)
              .filter(activity => activity.date.getTime() > 0)
              // Sort by date descending (most recent first)
              .sort((a, b) => b.date.getTime() - a.date.getTime());

              const displayedActivities = allActivities.slice(0, activitiesDisplayLimit);
              const hasMore = allActivities.length > activitiesDisplayLimit;

              return (
                <>
                  <div className="space-y-3">
                    {displayedActivities.map((activity) => {
                      if (activity.type === 'checklist') {
                        const inspection = activity.data as ChecklistCompletion;
                        return (
                          <div
                            key={activity.id}
                            className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 transition-colors cursor-pointer"
                            onClick={() => handleViewCompletion(inspection, 'checklist')}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                                  <ClipboardList className="w-4 h-4 text-primary-600" />
                                  {inspection.checklistName}
                                </h3>
                                <p className="text-xs text-gray-500 mt-1">
                                  Completed by: {(() => {
                                    const completedBy = inspection.completedByName || inspection.completedBy;
                                    if (!completedBy) return 'Unknown';
                                    if (typeof completedBy === 'object') return '[Object]';
                                    return String(completedBy);
                                  })()} • {formatDate(inspection.completedAt)}
                                </p>
                              </div>
                              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">
                                Completed
                              </span>
                            </div>
                            
                            {/* Checklist item status */}
                            {(() => {
                              // Use itemResults (new format) if available, otherwise fall back to items (legacy format)
                              const itemResults = inspection.itemResults;
                              const legacyItems = inspection.items;
                              
                              let completedCount = 0;
                              let totalCount = 0;
                              
                              if (itemResults) {
                                // New format: itemResults has all items with their status
                                const items = Object.values(itemResults);
                                totalCount = items.length;
                                completedCount = items.filter(r => r.status === 'PASS' || r.status === 'NA').length;
                              } else if (legacyItems) {
                                // Legacy format: items only contains completed items
                                completedCount = Object.values(legacyItems).filter(Boolean).length;
                                
                                // Try to get total count from:
                                // 1. Checklist from checklists array (already loaded - most reliable)
                                // 2. Loaded checklist item counts
                                // 3. Fall back to items length (incomplete, but better than nothing)
                                if (inspection.checklistId) {
                                  // First try to find the checklist in the already-loaded checklists array
                                  const checklist = checklists.find(c => c.id === inspection.checklistId);
                                  if (checklist && checklist.items) {
                                    totalCount = checklist.items.length;
                                    console.log(`[AssetRecordPage] Using checklist from array for ${inspection.checklistId}: ${totalCount} items`);
                                  } else if (checklistItemCounts[inspection.checklistId]) {
                                    // Use loaded counts
                                    totalCount = checklistItemCounts[inspection.checklistId];
                                    console.log(`[AssetRecordPage] Using loaded count for ${inspection.checklistId}: ${totalCount} items`);
                                  } else {
                                    // Fallback: use items length (incomplete, but shows something)
                                    totalCount = Object.keys(legacyItems).length;
                                    console.log(`[AssetRecordPage] Using fallback count for ${inspection.checklistId}: ${totalCount} items (checklist not loaded yet)`);
                                  }
                                } else {
                                  totalCount = Object.keys(legacyItems).length;
                                }
                              }
                              
                              // Show completion summary if available
                              const summary = inspection.completionSummary;
                              const hasIssues = summary && (
                                summary.incompleteItems > 0 ||
                                summary.missingRequiredFields.length > 0 ||
                                summary.missingRequiredEvidence.length > 0 ||
                                summary.needsReviewItems.length > 0
                              );
                              
                              if (totalCount > 0 || summary) {
                                return (
                                  <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                                    {totalCount > 0 && (
                                      <div className="flex items-center gap-2 text-xs text-gray-600">
                                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                                        <span>
                                          {completedCount} / {totalCount} items completed
                                        </span>
                                      </div>
                                    )}
                                    
                                    {/* Show summary of missing items if available */}
                                    {summary && hasIssues && (
                                      <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                                        <div className="font-semibold text-amber-900 mb-1 flex items-center gap-1">
                                          <AlertCircle className="w-3 h-3" />
                                          Checklist needs review
                                        </div>
                                        <div className="space-y-1 text-amber-800">
                                          {summary.incompleteItems > 0 && (
                                            <div>
                                              • {summary.incompleteItems} item{summary.incompleteItems !== 1 ? 's' : ''} incomplete
                                            </div>
                                          )}
                                          {summary.missingRequiredFields.length > 0 && (
                                            <div>
                                              • {summary.missingRequiredFields.length} item{summary.missingRequiredFields.length !== 1 ? 's' : ''} missing required fields
                                            </div>
                                          )}
                                          {summary.missingRequiredEvidence.length > 0 && (
                                            <div>
                                              • {summary.missingRequiredEvidence.length} item{summary.missingRequiredEvidence.length !== 1 ? 's' : ''} missing required evidence
                                            </div>
                                          )}
                                          {summary.needsReviewItems.length > 0 && (
                                            <div>
                                              • {summary.needsReviewItems.length} item{summary.needsReviewItems.length !== 1 ? 's' : ''} needs review
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        );
                      } else {
                        const formCompletion = activity.data as FormCompletion;
                        return (
                          <div
                            key={activity.id}
                            className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 transition-colors cursor-pointer"
                            onClick={() => handleViewCompletion(formCompletion, 'form')}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                                  <FileText className="w-4 h-4 text-primary-600" />
                                  {formCompletion.formName}
                                </h3>
                                <p className="text-xs text-gray-500 mt-1">
                                  Completed by: {(() => {
                                    const completedBy = formCompletion.completedBy;
                                    if (!completedBy) return 'Unknown';
                                    if (typeof completedBy === 'object') return '[Object]';
                                    return String(completedBy);
                                  })()} • {formatDate(formCompletion.completionDate)}
                                </p>
                              </div>
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                                Submitted
                              </span>
                            </div>
                          </div>
                        );
                      }
                    })}
                  </div>
                  
                  {/* Load More Button */}
                  {hasMore && (
                    <div className="mt-4 text-center">
                      <button
                        onClick={() => setActivitiesDisplayLimit(prev => prev + 10)}
                        className="px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
                      >
                        Load 10 More Activities ({allActivities.length - activitiesDisplayLimit} remaining)
                      </button>
                    </div>
                  )}
                  
                  {/* Show total count */}
                  {allActivities.length > 0 && (
                    <div className="mt-3 text-center text-xs text-gray-500">
                      Showing {displayedActivities.length} of {allActivities.length} activities
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        {/* Right Column - QR Code */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
            {/* Complin Logo */}
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-5 h-5 rounded-full bg-black flex-shrink-0"></div>
              <span className="text-xl font-bold text-gray-900 lowercase" style={{ letterSpacing: '-0.5px', fontFamily: 'Inter, sans-serif' }}>complin</span>
            </div>
            <div className="flex justify-center mb-4">
              <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                <QRCodeSVG
                  id="qr-code"
                  value={qrCodeUrl}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              </div>
            </div>
            {asset && (
              <p className="text-xs text-gray-700 text-center mb-4 font-medium lowercase">
                {asset.assetName}
              </p>
            )}
            <p className="text-xs text-gray-500 text-center mb-4">
              Scan this code to access this asset's record
            </p>
            <div className="space-y-2">
              <button
                onClick={handleDownloadQR}
                className="w-full btn-secondary px-4 py-2 flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download QR Code
              </button>
              <button
                onClick={handlePrintQR}
                className="w-full btn-secondary px-4 py-2 flex items-center justify-center gap-2"
              >
                <Printer className="w-4 h-4" />
                Print QR Code
              </button>
            </div>
            
            {/* Asset Info Card */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Quick Info</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Type:</span>
                  <span className="font-medium text-gray-900">{assetType.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Location:</span>
                  <span className="font-medium text-gray-900">{location?.name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Asset Name:</span>
                  <span className="font-medium text-gray-900">{asset.assetName}</span>
                </div>
                {asset.assetLocation && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Site Location:</span>
                    <span className="font-medium text-gray-900">{asset.assetLocation}</span>
                  </div>
                )}
                {asset.floorId && taggedFloor && asset.locationId && (
                  <a
                    href={`/floor-map/full?locationId=${asset.locationId}&floorId=${asset.floorId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex justify-between items-center hover:opacity-80"
                  >
                    <span className="text-gray-600">On Floor Map:</span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-green-100 text-green-800 text-sm font-medium hover:bg-green-200">
                      <MapPin className="w-3.5 h-3.5" />
                      {taggedFloor.name}
                    </span>
                  </a>
                )}
              </div>

              {/* Custom Fields in Sidebar */}
              {assetType.customFields && assetType.customFields.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-700">Custom Fields</h3>
                    {!isEditingFields ? (
                      <button
                        onClick={handleStartEditFields}
                        className="text-primary-600 hover:text-primary-700 p-1"
                        title="Edit all fields"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    ) : (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={handleSaveFields}
                          className="text-green-600 hover:text-green-700 p-1"
                          title="Save changes"
                          disabled={isSavingFields}
                        >
                          <Save className="w-4 h-4" />
                        </button>
                        <button
                          onClick={handleCancelEditFields}
                          className="text-red-600 hover:text-red-700 p-1"
                          title="Cancel editing"
                          disabled={isSavingFields}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="space-y-3 text-sm">
                    {assetType.customFields.map((field, index) => {
                      const currentValue = isEditingFields 
                        ? editingFields[field.name] || ''
                        : asset.fieldValues?.[field.name] || '';
                      
                      return (
                        <div key={field.id || index} className="pb-2 border-b border-gray-100 last:border-b-0">
                          <div className="flex items-start justify-between mb-1">
                            <label className="text-gray-600 text-xs font-medium">
                              {field.name}
                              {field.required && <span className="text-red-500 ml-0.5">*</span>}
                            </label>
                          </div>
                          {isEditingFields ? (
                            field.type === 'loan_officer_picker' ? (
                              <UserRolePicker
                                field={field}
                                value={currentValue}
                                onChange={(userId) => handleFieldValueChange(field.name, userId || '')}
                                required={field.required}
                              />
                            ) : field.type === 'textarea' ? (
                              <textarea
                                value={currentValue}
                                onChange={(e) => handleFieldValueChange(field.name, e.target.value)}
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                                placeholder={`Enter ${field.name.toLowerCase()}`}
                                rows={2}
                                required={field.required}
                              />
                            ) : field.type === 'alphanumeric' ? (
                              <input
                                type="text"
                                value={currentValue}
                                onChange={(e) => {
                                  // Validate alphanumeric input
                                  const value = e.target.value;
                                  const pattern = /^[A-Za-z0-9]*$/;
                                  if (pattern.test(value) || value === '') {
                                    handleFieldValueChange(field.name, value);
                                  }
                                }}
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                                placeholder={`Enter ${field.name.toLowerCase()} (letters and numbers only)`}
                                required={field.required}
                                pattern={field.metadata?.validation?.pattern || '^[A-Za-z0-9]+$'}
                              />
                            ) : (
                              <input
                                type={field.type === 'number' ? 'number' : 
                                      field.type === 'date' ? 'date' : 
                                      field.type === 'email' ? 'email' : 
                                      field.type === 'tel' ? 'tel' : 'text'}
                                value={currentValue}
                                onChange={(e) => handleFieldValueChange(field.name, e.target.value)}
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                                placeholder={`Enter ${field.name.toLowerCase()}`}
                                required={field.required}
                              />
                            )
                          ) : (
                            <p className="font-medium text-gray-900 text-xs break-words">
                              {currentValue || <span className="text-gray-400 italic">Not set</span>}
                            </p>
                          )}
                          {field.description && !isEditingFields && (
                            <p className="text-xs text-gray-400 mt-0.5">{field.description}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {isSavingFields && (
                    <div className="mt-2 text-center">
                      <span className="text-xs text-gray-500">Saving...</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Backdrop overlay when modals are open */}
      {(isRunningChecklist || isRunningForm) && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-40 transition-opacity" />
      )}

      {/* Run Checklist Modal */}
      {isRunningChecklist && selectedChecklist && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <RunChecklist
              checklist={selectedChecklist}
              assetId={assetId}
              onClose={() => {
                setIsRunningChecklist(false);
                setSelectedChecklist(null);
              }}
              onComplete={handleChecklistComplete}
            />
          </div>
        </div>
      )}

      {/* Run Form Modal */}
      {isRunningForm && selectedForm && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <RunForm
              form={selectedForm}
              assetId={assetId}
              onClose={() => {
                setIsRunningForm(false);
                setSelectedForm(null);
              }}
              onComplete={handleFormComplete}
            />
          </div>
        </div>
      )}

      {/* Completion Details Right-Side Panel */}
      {selectedCompletion && (
        <>
          {/* Click-out overlay to close */}
          <div
            className="fixed inset-0 bg-black bg-opacity-20 z-[125]"
            onClick={handleCloseCompletionDetails}
          />
          <div className="fixed inset-y-0 right-0 w-[500px] bg-white shadow-2xl border-l border-gray-200 z-[130] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">
                  {selectedCompletionType === 'checklist' ? 'Checklist' : 'Form'} Run Details
                </h3>
                <p className="text-xs text-gray-600 mt-0.5">
                  {formatDate(
                    selectedCompletionType === 'checklist'
                      ? (selectedCompletion as ChecklistCompletion).completedAt
                      : (selectedCompletion as FormCompletion).completionDate
                  )}
                </p>
              </div>
              <button
                onClick={handleCloseCompletionDetails}
                className="text-gray-500 hover:text-gray-700 p-1"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
              {isLoadingCompletionDetails ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading details...</p>
                </div>
              ) : selectedCompletionType === 'checklist' && viewingChecklist && selectedCompletion ? (
                <>
                  {/* Checklist Completion Info */}
                  <div className="bg-gray-50 border border-gray-200 rounded p-2">
                    <div className="space-y-1 text-xs">
                      <div>
                        <span className="font-medium text-gray-700">Checklist:</span>{' '}
                        <span className="text-gray-900">{(selectedCompletion as ChecklistCompletion).checklistName}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Location:</span>{' '}
                        <span className="text-gray-900">{(selectedCompletion as ChecklistCompletion).locationName || 'Unknown'}</span>
                      </div>
                      {assetId && (
                        <>
                          <div>
                            <span className="font-medium text-gray-700">Asset:</span>{' '}
                            <span className="text-gray-900">{(selectedCompletion as ChecklistCompletion).assetName || asset?.assetName || '—'}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Asset ID:</span>{' '}
                            <span className="text-gray-900 font-mono">{(selectedCompletion as ChecklistCompletion).assetId || (selectedCompletion as ChecklistCompletion).variableValues?.['assetId'] || assetId || '—'}</span>
                          </div>
                        </>
                      )}
                      <div>
                        <span className="font-medium text-gray-700">Completed By:</span>{' '}
                        <span className="text-gray-900">
                          {(() => {
                            const completedBy = (selectedCompletion as ChecklistCompletion).completedByName || (selectedCompletion as ChecklistCompletion).completedBy;
                            if (!completedBy) return 'Unknown';
                            if (typeof completedBy === 'object') return '[Object]';
                            return String(completedBy);
                          })()}
                        </span>
                      </div>
                      {/* Time Tracking Information */}
                      {(() => {
                        const completion = selectedCompletion as ChecklistCompletion;
                        const activeTime = completion.activeTimeSeconds !== undefined ? Number(completion.activeTimeSeconds) : null;
                        const totalTime = completion.totalTimeSeconds !== undefined ? Number(completion.totalTimeSeconds) : null;
                        const pauseCount = completion.pauseCount !== undefined ? Number(completion.pauseCount) : null;
                        
                        return (
                          <>
                            {activeTime !== null && (
                              <div>
                                <span className="font-medium text-gray-700">Active Time:</span>{' '}
                                <span className="text-gray-900">{formatTimeDisplay(activeTime)}</span>
                              </div>
                            )}
                            {totalTime !== null && (
                              <div>
                                <span className="font-medium text-gray-700">Total Time:</span>{' '}
                                <span className="text-gray-900">{formatTimeDisplay(totalTime)}</span>
                              </div>
                            )}
                            {pauseCount !== null && pauseCount > 0 && (
                              <div>
                                <span className="font-medium text-gray-700">Pauses:</span>{' '}
                                <span className="text-gray-900">{pauseCount}</span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Variables */}
                  {(selectedCompletion as ChecklistCompletion).variableValues && Object.keys((selectedCompletion as ChecklistCompletion).variableValues || {}).length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-2">
                      <h4 className="text-xs font-semibold text-gray-800 mb-1">Variables</h4>
                      <div className="space-y-1">
                        {Object.entries((selectedCompletion as ChecklistCompletion).variableValues || {}).map(([key, value]) => (
                          <div key={key} className="text-xs">
                            <span className="font-medium text-gray-700">{key}:</span>{' '}
                            <span className="text-gray-900">
                              {value === null || value === undefined 
                                ? '—' 
                                : typeof value === 'object' 
                                  ? '[Object]' 
                                  : String(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Checklist Items */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-800 mb-1.5">
                      Checklist Items ({
                        (selectedCompletion as ChecklistCompletion).items
                          ? Object.values((selectedCompletion as ChecklistCompletion).items || {}).filter((v: any) => v).length
                          : 0
                      }/{viewingChecklist.items?.length || 0} completed)
                    </h4>
                    <div className="space-y-2 border border-gray-200 rounded p-2 max-h-[600px] overflow-y-auto">
                      {viewingChecklist.items?.map((item, index) => {
                        const completion = selectedCompletion as ChecklistCompletion;
                        // Use items (mobile format)
                        const items = completion.items || {};
                        const isCompleted = items[item.text] || false;
                        const photoData = completion.photos?.[item.text];
                        // Normalize photos to array: handle both string (single photo) and string[] (multiple photos)
                        const photos = photoData 
                          ? (Array.isArray(photoData) ? photoData : [photoData])
                          : [];
                        const taskRefs = (completion.itemTaskReferences as Record<string, string[]>)?.[item.text] || [];
                        // Get item result for finding/status
                        const itemResult = completion.itemResults?.[item.text];
                        // Get evidence for this item
                        const evidence = itemEvidenceMap[item.text] || [];
                        // Get validation result
                        const validation = itemValidationResults[item.text];
                        const isExpanded = expandedItems.has(item.text);
                        
                        const toggleExpand = () => {
                          setExpandedItems(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(item.text)) {
                              newSet.delete(item.text);
                            } else {
                              newSet.add(item.text);
                            }
                            return newSet;
                          });
                        };
                        
                        // Determine border color based on validation status
                        const borderColor = validation?.status === 'INCOMPLETE' 
                          ? 'border-red-300' 
                          : validation?.status === 'NEEDS_REVIEW'
                          ? 'border-orange-300'
                          : 'border-gray-200';
                        
                        return (
                          <div 
                            key={`${item.text}-${index}`} 
                            className={`bg-gray-50 rounded border-2 ${borderColor} overflow-hidden`}
                          >
                            {/* Item Header - Always Visible */}
                            <div 
                              className="flex items-start gap-2 p-1.5 cursor-pointer hover:bg-gray-100 transition-colors"
                              onClick={toggleExpand}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="text-xs text-gray-900 flex items-center gap-1">
                                  <span className="font-medium">{index + 1}.</span> 
                                  <span className="flex-1">{item.text}</span>
                                  {isCompleted ? (
                                    <span className="text-green-700">✓</span>
                                  ) : (
                                    <span className="text-gray-400">•</span>
                                  )}
                                  <span className="text-gray-400 text-[10px] ml-1">
                                    {isExpanded ? '▼' : '▶'}
                                  </span>
                                </div>
                                
                                {/* Status and Finding - Always Visible */}
                                {itemResult && (
                                  <div className="mt-1 text-xs">
                                    <div className="flex items-center gap-2">
                                      <span className={`font-semibold ${
                                        itemResult.status === 'PASS' ? 'text-green-700' :
                                        itemResult.status === 'FAIL' ? 'text-red-700' :
                                        itemResult.status === 'NA' ? 'text-gray-600' :
                                        'text-orange-600'
                                      }`}>
                                        Status: {itemResult.status}
                                      </span>
                                      {validation && validation.status !== 'COMPLETE' && (
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                          validation.status === 'INCOMPLETE' 
                                            ? 'bg-red-100 text-red-700' 
                                            : 'bg-orange-100 text-orange-700'
                                        }`}>
                                          {validation.status === 'INCOMPLETE' ? '⚠ Incomplete' : '🔍 Needs Review'}
                                        </span>
                                      )}
                                    </div>
                                    {itemResult.findingText && (
                                      <div className="text-orange-600 italic mt-0.5">
                                        {itemResult.findingText}
                                      </div>
                                    )}
                                    {validation?.suggestedStatus && (
                                      <div className="text-blue-600 text-[10px] mt-0.5">
                                        💡 Suggested: {validation.suggestedStatus} - {validation.suggestedReason}
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {/* Quick Info - Always Visible */}
                                {isCompleted && taskRefs.length > 0 && (
                                  <div className="mt-1 text-xs text-blue-600 font-medium">
                                    ✓ Completed by creating task{taskRefs.length > 1 ? 's' : ''} ({taskRefs.length} task{taskRefs.length > 1 ? 's' : ''} created)
                                  </div>
                                )}
                                {evidence.length > 0 && !isExpanded && (
                                  <div className="mt-1 text-xs text-gray-500">
                                    {evidence.length} evidence item{evidence.length !== 1 ? 's' : ''} • Click to expand
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Expanded Content */}
                            {isExpanded && (
                              <div className="border-t border-gray-200 p-2 space-y-3 bg-white">
                                {/* Photos */}
                                {photos.length > 0 && (
                                  <div>
                                    <h5 className="text-xs font-semibold text-gray-700 mb-1">Photos</h5>
                                    <div className="flex flex-wrap gap-2">
                                      {photos.map((photoUrl, photoIndex) => (
                                        <img
                                          key={photoIndex}
                                          src={photoUrl}
                                          alt={`${item.text} photo ${photoIndex + 1}`}
                                          className="max-w-full h-auto rounded border border-gray-300 max-h-32 cursor-pointer"
                                          onClick={() => window.open(photoUrl, '_blank')}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Required Evidence and Fields */}
                                {(() => {
                                  // Show evidence definitions (metadata only - which evidence is required/captured)
                                  if (!item.evidenceDefinitions || item.evidenceDefinitions.length === 0) {
                                    return null;
                                  }
                                  
                                  // Collect all fields from all evidence definitions
                                  const allFields: Array<{
                                    fieldDef: any;
                                    evidenceDef: any;
                                    evDefIndex: number;
                                  }> = [];
                                  
                                  item.evidenceDefinitions.forEach((evidenceDef, evDefIndex) => {
                                    if (evidenceDef.fieldDefinitions && evidenceDef.fieldDefinitions.length > 0) {
                                      evidenceDef.fieldDefinitions.forEach((fieldDef: any) => {
                                        allFields.push({ fieldDef, evidenceDef, evDefIndex });
                                      });
                                    }
                                  });
                                  
                                  const requiredCount = allFields.filter(({ fieldDef, evidenceDef }) => 
                                    fieldDef.required === true && (evidenceDef.required === true || fieldDef.required === true)
                                  ).length;
                                  
                                  return (
                                    <>
                                      {/* Required Evidence Section */}
                                      <div>
                                        <h5 className="text-xs font-semibold text-gray-700 mb-1">
                                          Required Evidence ({item.evidenceDefinitions.length})
                                        </h5>
                                        <div className="space-y-3 border border-gray-200 rounded p-2 bg-gray-50">
                                          {item.evidenceDefinitions.map((evidenceDef, evDefIndex) => {
                                            // Get field keys for this evidence definition
                                            const evidenceFieldKeys = new Set(
                                              evidenceDef.fieldDefinitions?.map((fd: any) => fd.key) || []
                                            );
                                            
                                            // Determine which files are actually being used for field values
                                            // by checking which file provides the value that's actually used for each field
                                            const filesUsedForFields = new Set<string>();
                                            
                                            // For each field in this evidence definition, find which file provides the value being used
                                            evidenceDef.fieldDefinitions?.forEach((fieldDef: any) => {
                                              let fieldValue: any = undefined;
                                              let sourceFile: string | undefined = undefined;
                                              
                                              // Try evidence fields first (most specific)
                                              evidence.forEach(ev => {
                                                const evName = ev.evidenceName || (ev as any).categoryCode;
                                                const defName = evidenceDef.evidenceName || (evidenceDef as any).categoryCode;
                                                if (evName === defName && 
                                                    ev.notes && ev.notes.includes('Auto-extracted fields:')) {
                                                  try {
                                                    const fieldsMatch = ev.notes.match(/Auto-extracted fields: (.+)/);
                                                    if (fieldsMatch) {
                                                      const fields = JSON.parse(fieldsMatch[1]);
                                                      if (fields[fieldDef.key] !== undefined && 
                                                          fields[fieldDef.key] !== null && 
                                                          fields[fieldDef.key] !== '') {
                                                        fieldValue = fields[fieldDef.key];
                                                        sourceFile = ev.documentName || ev.fileUrl;
                                                      }
                                                    }
                                                  } catch (error) {
                                                    // Ignore parse errors
                                                  }
                                                }
                                              });
                                              
                                              // Try asset facts if no value from evidence
                                              if (!fieldValue) {
                                                const fact = assetFacts[fieldDef.key];
                                                if (fact && fact.value !== null && fact.value !== undefined && fact.value !== '') {
                                                  // Get the primary file (highest confidence) from extractedValues
                                                  if (fact.extractedValues && fact.extractedValues.length > 0) {
                                                    // Sort by confidence and get the primary file
                                                    const sorted = fact.extractedValues.sort((a: any, b: any) => {
                                                      const confMap: Record<string, number> = { high: 3, medium: 2, low: 1 };
                                                      return (confMap[b.confidence || 'low'] || 0) - (confMap[a.confidence || 'low'] || 0);
                                                    });
                                                    sourceFile = sorted[0].fileName;
                                                  } else if (fact.extractedFrom) {
                                                    sourceFile = fact.extractedFrom;
                                                  }
                                                }
                                              }
                                              
                                              // If we found a source file, add it to the set
                                              if (sourceFile) {
                                                filesUsedForFields.add(sourceFile);
                                              }
                                            });
                                            
                                            // Only show evidence records that match files actually being used
                                            const matchingEvidence = evidence.filter(ev => {
                                              const evName = ev.evidenceName || (ev as any).categoryCode;
                                              const defName = evidenceDef.evidenceName || (evidenceDef as any).categoryCode;
                                              if (evName !== defName) {
                                                return false;
                                              }
                                              
                                              // Check if this evidence's file is in the set of files being used
                                              const evFileName = ev.documentName || ev.fileUrl;
                                              return evFileName && filesUsedForFields.has(evFileName);
                                            });
                                            
                                            return (
                                              <div key={evDefIndex} className="bg-white border border-gray-200 rounded p-2">
                                                {/* Evidence Definition Header */}
                                                <div className="flex items-start justify-between mb-2">
                                                  <div className="flex-1">
                                                    <div className="flex items-center gap-1.5">
                                                      <span className="text-xs font-semibold text-gray-700">
                                                        {evidenceDef.evidenceName || (evidenceDef as any).categoryCode || 'Unknown Evidence'}
                                                      </span>
                                                      {evidenceDef.required && (
                                                        <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] rounded font-medium">
                                                          Required
                                                        </span>
                                                      )}
                                                      {!evidenceDef.required && (
                                                        <span className="px-1.5 py-0.5 bg-gray-200 text-gray-600 text-[10px] rounded">
                                                          Optional
                                                        </span>
                                                      )}
                                                      {matchingEvidence.length > 0 && (
                                                        <span className="text-[10px] text-green-600">
                                                          ✓ {matchingEvidence.length} file{matchingEvidence.length !== 1 ? 's' : ''} used
                                                        </span>
                                                      )}
                                                    </div>
                                                    {evidenceDef.description && (
                                                      <div className="text-[10px] text-gray-600 italic mt-0.5">
                                                        {evidenceDef.description}
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                                
                                                {/* Show captured evidence details - only files with field values */}
                                                {matchingEvidence.length > 0 && (
                                                  <div className="mt-2 pt-2 border-t border-gray-200">
                                                    <div className="text-[10px] font-medium text-gray-600 mb-1">
                                                      Files Used for Fields ({matchingEvidence.length})
                                                    </div>
                                                    <div className="space-y-1">
                                                      {matchingEvidence.map((ev, evIndex) => {
                                                        // Extract which fields are provided by this evidence
                                                        const providedFields: string[] = [];
                                                        if (ev.notes && ev.notes.includes('Auto-extracted fields:')) {
                                                          try {
                                                            const fieldsMatch = ev.notes.match(/Auto-extracted fields: (.+)/);
                                                            if (fieldsMatch) {
                                                              const fields = JSON.parse(fieldsMatch[1]);
                                                              Object.keys(fields).forEach(key => {
                                                                if (evidenceFieldKeys.has(key) && 
                                                                    fields[key] !== null && 
                                                                    fields[key] !== undefined && 
                                                                    fields[key] !== '') {
                                                                  providedFields.push(key);
                                                                }
                                                              });
                                                            }
                                                          } catch (error) {
                                                            // Ignore parse errors
                                                          }
                                                        }
                                                        
                                                        return (
                                                          <div key={ev.id || evIndex} className="text-[10px] bg-blue-50 p-1.5 rounded border border-blue-200">
                                                            {ev.documentName && (
                                                              <div className="font-medium text-gray-700">{ev.documentName}</div>
                                                            )}
                                                            {providedFields.length > 0 && (
                                                              <div className="text-[9px] text-gray-600 mt-0.5">
                                                                Provides: {providedFields.length} field{providedFields.length !== 1 ? 's' : ''}
                                                              </div>
                                                            )}
                                                            {ev.fileUrl && (
                                                              <a
                                                                href={ev.fileUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-blue-600 hover:text-blue-800 underline text-[10px] mt-0.5 inline-block"
                                                                onClick={(e) => e.stopPropagation()}
                                                              >
                                                                View File
                                                              </a>
                                                            )}
                                                          </div>
                                                        );
                                                      })}
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                      
                                      {/* Fields Section */}
                                      {allFields.length > 0 && (
                                        <div className="mt-4">
                                          <h5 className="text-xs font-semibold text-gray-700 mb-1">
                                            Fields ({allFields.length} total, {requiredCount} required)
                                          </h5>
                                          <div className="space-y-1.5 border border-gray-200 rounded p-2 bg-gray-50">
                                            {allFields.map(({ fieldDef, evidenceDef, evDefIndex }, idx) => {
                                              // Check if field is required
                                              const isRequired = fieldDef.required === true && (evidenceDef.required === true || fieldDef.required === true);
                                              const isRequiredByValidation = validation?.missingFields.includes(fieldDef.key) || false;
                                              const isRequiredFinal = isRequired || isRequiredByValidation;
                                              
                                              // Try to get field value from various sources
                                              let fieldValue: any = undefined;
                                              let valueSource = '';
                                              let confidence: number | undefined = undefined;
                                              
                                              // Try evidence fields first
                                              evidence.forEach(ev => {
                                                if (ev.notes && ev.notes.includes('Auto-extracted fields:')) {
                                                  try {
                                                    const fieldsMatch = ev.notes.match(/Auto-extracted fields: (.+)/);
                                                    if (fieldsMatch) {
                                                      const fields = JSON.parse(fieldsMatch[1]);
                                                      if (fields[fieldDef.key] !== undefined && fields[fieldDef.key] !== null && fields[fieldDef.key] !== '') {
                                                        fieldValue = fields[fieldDef.key];
                                                        valueSource = 'Evidence';
                                                      }
                                                    }
                                                  } catch (error) {
                                                    // Ignore parse errors
                                                  }
                                                }
                                              });
                                              
                                              // Try asset facts (check by exact key match)
                                              // Support multiple values from different files
                                              let allExtractedValues: Array<{
                                                value: any;
                                                fileName: string;
                                                confidence?: 'high' | 'medium' | 'low';
                                              }> = [];
                                              
                                              // Check asset facts - use if no value found yet, or if found value is null/empty
                                              const fact = assetFacts[fieldDef.key];
                                              if (fact && fact.value !== null && fact.value !== undefined && fact.value !== '') {
                                                // Use asset fact value if:
                                                // 1. No value found yet, OR
                                                // 2. Found value is null/empty (asset facts take precedence)
                                                if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
                                                  fieldValue = fact.value;
                                                  valueSource = 'Asset Facts';
                                                  // Convert string confidence to number
                                                  if (typeof fact.confidence === 'string') {
                                                    confidence = fact.confidence === 'high' ? 0.9 : fact.confidence === 'medium' ? 0.7 : 0.5;
                                                  } else {
                                                    confidence = fact.confidence;
                                                  }
                                                }
                                                
                                                // Collect all extracted values from different files
                                                if (fact.extractedValues && fact.extractedValues.length > 0) {
                                                  allExtractedValues = fact.extractedValues.map((ev: any) => ({
                                                    value: ev.value,
                                                    fileName: ev.fileName,
                                                    confidence: ev.confidence,
                                                  }));
                                                } else if (fact.extractedFrom) {
                                                  // Legacy: single extracted value
                                                  allExtractedValues = [{
                                                    value: fact.value,
                                                    fileName: fact.extractedFrom,
                                                    confidence: fact.confidence,
                                                  }];
                                                }
                                              }
                                              
                                              // Debug logging for missing required fields
                                              if (!fieldValue && isRequiredFinal) {
                                                const factKeys = Object.keys(assetFacts);
                                                const matchingKey = factKeys.find(k => 
                                                  k.toLowerCase() === fieldDef.key.toLowerCase() ||
                                                  k.replace(/\s+/g, '') === fieldDef.key.replace(/\s+/g, '') ||
                                                  k.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() === fieldDef.key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
                                                );
                                                if (matchingKey && matchingKey !== fieldDef.key) {
                                                  console.warn(`[AssetRecordPage] Field "${fieldDef.label}" key mismatch: looking for "${fieldDef.key}" but found "${matchingKey}" in asset facts`);
                                                  // Try using the matching key
                                                  const matchedFact = assetFacts[matchingKey];
                                                  if (matchedFact && matchedFact.value !== null && matchedFact.value !== undefined && matchedFact.value !== '') {
                                                    fieldValue = matchedFact.value;
                                                    valueSource = 'Asset Facts (matched)';
                                                    if (typeof matchedFact.confidence === 'string') {
                                                      confidence = matchedFact.confidence === 'high' ? 0.9 : matchedFact.confidence === 'medium' ? 0.7 : 0.5;
                                                    } else {
                                                      confidence = matchedFact.confidence;
                                                    }
                                                  }
                                                } else if (!fact) {
                                                  console.warn(`[AssetRecordPage] Field "${fieldDef.label}" (key: "${fieldDef.key}") not found in asset facts. Available keys:`, factKeys.slice(0, 10));
                                                } else if (fact && (fact.value === null || fact.value === undefined || fact.value === '')) {
                                                  console.warn(`[AssetRecordPage] Field "${fieldDef.label}" (key: "${fieldDef.key}") found in asset facts but value is empty/null`);
                                                }
                                              }
                                              
                                              // Try item result computed checks
                                              if (fieldValue === undefined && itemResult?.computedChecks) {
                                                const computedKey = fieldDef.key.replace(/\./g, '_');
                                                if ((itemResult.computedChecks as any)[computedKey] !== undefined) {
                                                  fieldValue = (itemResult.computedChecks as any)[computedKey];
                                                  valueSource = 'Computed';
                                                }
                                              }
                                              
                                              const isCaptured = fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
                                              const lowConfidence = validation?.lowConfidenceFields.find(f => f.key === fieldDef.key);
                                              
                                              // Format value for display
                                              let displayValue = '';
                                              if (isCaptured) {
                                                if (fieldDef.type === 'date' && fieldValue) {
                                                  try {
                                                    const date = fieldValue instanceof Date ? fieldValue : new Date(fieldValue);
                                                    displayValue = date.toLocaleDateString();
                                                  } catch {
                                                    displayValue = String(fieldValue);
                                                  }
                                                } else if (fieldDef.type === 'boolean') {
                                                  displayValue = fieldValue ? 'Yes' : 'No';
                                                } else {
                                                  displayValue = String(fieldValue);
                                                }
                                              }
                                              
                                              return (
                                                <div 
                                                  key={`${evDefIndex}-${evidenceDef.categoryCode}-${fieldDef.key}-${idx}`}
                                                className={`p-1.5 rounded border ${
                                                    isCaptured 
                                                      ? 'bg-green-50 border-green-200' 
                                                      : isRequiredFinal
                                                      ? 'bg-red-50 border-red-200'
                                                      : 'bg-gray-50 border-gray-200'
                                                  }`}
                                                >
                                                  <div className="flex items-start justify-between gap-2">
                                                    <div className="flex-1 min-w-0">
                                                      <div className="flex items-center gap-1.5 flex-wrap">
                                                        {isCaptured ? (
                                                          <span className="text-green-700 text-xs">✓</span>
                                                        ) : (
                                                          <span className={`text-xs ${
                                                            isRequiredFinal ? 'text-red-700' : 'text-gray-400'
                                                          }`}>✗</span>
                                                        )}
                                                        <span className={`text-xs font-medium ${
                                                          isCaptured ? 'text-gray-700' : 
                                                          isRequiredFinal ? 'text-red-700' : 'text-gray-600'
                                                        }`}>
                                                          {fieldDef.label}
                                                        </span>
                                                        {isRequiredFinal && (
                                                          <span className="px-1 py-0.5 bg-red-100 text-red-700 text-[10px] rounded font-medium">
                                                            Required
                                                          </span>
                                                        )}
                                                        {!isRequiredFinal && (
                                                          <span className="px-1 py-0.5 bg-gray-200 text-gray-600 text-[10px] rounded">
                                                            Optional
                                                          </span>
                                                        )}
                                                        <span className="text-[10px] text-gray-500">
                                                          ({evidenceDef.categoryCode})
                                                        </span>
                                                      </div>
                                                      {isCaptured ? (
                                                        <div className="mt-0.5 ml-5">
                                                          <div className="text-xs text-gray-900">
                                                            {displayValue}
                                                          </div>
                                                          {valueSource && (
                                                            <div className="text-[10px] text-gray-500 mt-0.5">
                                                              Source: {valueSource}
                                                            </div>
                                                          )}
                                                          {confidence !== undefined && (
                                                            <div className={`text-[10px] mt-0.5 ${
                                                              confidence < 0.7
                                                                ? 'text-orange-600'
                                                                : 'text-gray-500'
                                                            }`}>
                                                              Confidence: {Math.round(confidence * 100)}%
                                                            </div>
                                                          )}
                                                          {lowConfidence && (
                                                            <div className="text-[10px] text-orange-600 mt-0.5">
                                                              ⚠ Needs review (below {Math.round(lowConfidence.threshold * 100)}% threshold)
                                                            </div>
                                                          )}
                                                          {/* Show source file for the primary value */}
                                                          {allExtractedValues.length > 0 && (() => {
                                                            // Get primary value (highest confidence)
                                                            const primaryExtractedValue = allExtractedValues.sort((a, b) => {
                                                              const confMap: Record<string, number> = { high: 3, medium: 2, low: 1 };
                                                              return (confMap[b.confidence || 'low'] || 0) - (confMap[a.confidence || 'low'] || 0);
                                                            })[0];
                                                            
                                                            if (primaryExtractedValue && primaryExtractedValue.fileName) {
                                                              return (
                                                                <div className="text-[10px] text-gray-500 mt-0.5">
                                                                  From: {primaryExtractedValue.fileName}
                                                                </div>
                                                              );
                                                            }
                                                            return null;
                                                          })()}
                                                        </div>
                                                      ) : (
                                                        <div className="mt-0.5 ml-5">
                                                          <div className={`text-xs italic ${
                                                            isRequiredFinal ? 'text-red-600' : 'text-gray-500'
                                                          }`}>
                                                            {isRequiredFinal ? 'Not available' : 'Not captured'}
                                                          </div>
                                                          {fieldDef.type && (
                                                            <div className="text-[10px] text-gray-500 mt-0.5">
                                                              Expected type: {fieldDef.type}
                                                            </div>
                                                          )}
                                                        </div>
                                                      )}
                                                    </div>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                                
                                {/* Validation Status */}
                                {validation && (
                                  <div>
                                    <h5 className="text-xs font-semibold text-gray-700 mb-1">Validation Status</h5>
                                    <div className={`p-2 rounded border ${
                                      validation.status === 'COMPLETE' 
                                        ? 'bg-green-50 border-green-200' 
                                        : validation.status === 'INCOMPLETE'
                                        ? 'bg-red-50 border-red-200'
                                        : 'bg-orange-50 border-orange-200'
                                    }`}>
                                      <div className="text-xs space-y-1">
                                        <div className="font-medium">
                                          Status: <span className={
                                            validation.status === 'COMPLETE' ? 'text-green-700' :
                                            validation.status === 'INCOMPLETE' ? 'text-red-700' :
                                            'text-orange-700'
                                          }>{validation.status}</span>
                                        </div>
                                        
                                        {validation.missingFields.length > 0 && (
                                          <div className="mt-2">
                                            <div className="font-medium text-red-700 mb-1">Missing Required Fields:</div>
                                            <ul className="list-disc list-inside space-y-0.5">
                                              {validation.missingFields.map((fieldKey, missingIdx) => {
                                                // Find field in evidenceDefinitions
                                                let fieldDef: any = null;
                                                if (item.evidenceDefinitions) {
                                                  for (const evidenceDef of item.evidenceDefinitions) {
                                                    fieldDef = evidenceDef.fieldDefinitions?.find((f: any) => f.key === fieldKey);
                                                    if (fieldDef) break;
                                                  }
                                                }
                                                return (
                                                  <li key={`${item.text}-missing-${fieldKey}-${missingIdx}`} className="text-red-600">
                                                    {fieldDef?.label || fieldKey}
                                                  </li>
                                                );
                                              })}
                                            </ul>
                                          </div>
                                        )}
                                        
                                        {validation.invalidFields.length > 0 && (
                                          <div className="mt-2">
                                            <div className="font-medium text-red-700 mb-1">Invalid Fields:</div>
                                            <ul className="list-disc list-inside space-y-0.5">
                                              {validation.invalidFields.map((field, invalidIdx) => (
                                                <li key={`${item.text}-invalid-${field.key}-${invalidIdx}`} className="text-red-600">
                                                  {field.key}: {field.reason}
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                        
                                        {validation.lowConfidenceFields.length > 0 && (
                                          <div className="mt-2">
                                            <div className="font-medium text-orange-700 mb-1">Low Confidence Fields (Require Review):</div>
                                            <ul className="list-disc list-inside space-y-0.5">
                                              {validation.lowConfidenceFields.map((field, lowConfIdx) => {
                                                // Find field in evidenceDefinitions
                                                let fieldDef: any = null;
                                                if (item.evidenceDefinitions) {
                                                  for (const evidenceDef of item.evidenceDefinitions) {
                                                    fieldDef = evidenceDef.fieldDefinitions?.find((f: any) => f.key === field.key);
                                                    if (fieldDef) break;
                                                  }
                                                }
                                                return (
                                                  <li key={`${item.text}-lowconf-${field.key}-${lowConfIdx}`} className="text-orange-600">
                                                    {fieldDef?.label || field.key}: {Math.round(field.confidence * 100)}% confidence (threshold: {Math.round(field.threshold * 100)}%)
                                                  </li>
                                                );
                                              })}
                                            </ul>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              ) : selectedCompletionType === 'form' && viewingForm && selectedCompletion ? (
                <>
                  {/* Form Completion Info */}
                  <div className="bg-gray-50 border border-gray-200 rounded p-2">
                    <div className="space-y-1 text-xs">
                      <div>
                        <span className="font-medium text-gray-700">Form:</span>{' '}
                        <span className="text-gray-900">{(selectedCompletion as FormCompletion).formName}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Location:</span>{' '}
                        <span className="text-gray-900">{(selectedCompletion as FormCompletion).locationName || 'Unknown'}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Completed By:</span>{' '}
                        <span className="text-gray-900">
                          {(() => {
                            const completedBy = (selectedCompletion as FormCompletion).completedBy;
                            if (!completedBy) return 'Unknown';
                            if (typeof completedBy === 'object') return '[Object]';
                            return String(completedBy);
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Variables */}
                  {(selectedCompletion as FormCompletion).variableValues && Object.keys((selectedCompletion as FormCompletion).variableValues || {}).length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-2">
                      <h4 className="text-xs font-semibold text-gray-800 mb-1">Variables</h4>
                      <div className="space-y-1">
                        {Object.entries((selectedCompletion as FormCompletion).variableValues || {}).map(([key, value]) => (
                          <div key={key} className="text-xs">
                            <span className="font-medium text-gray-700">{key}:</span>{' '}
                            <span className="text-gray-900">
                              {value === null || value === undefined 
                                ? '—' 
                                : typeof value === 'object' 
                                  ? '[Object]' 
                                  : String(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Form Fields */}
                  {(selectedCompletion as FormCompletion).fieldValues && Object.keys((selectedCompletion as FormCompletion).fieldValues || {}).length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-800 mb-1.5">Form Fields</h4>
                      <div className="space-y-2 border border-gray-200 rounded p-2 max-h-[400px] overflow-y-auto">
                        {viewingForm.fields?.map((field, index) => {
                          const fieldValue = (selectedCompletion as FormCompletion).fieldValues?.[field.label];
                          if (fieldValue === undefined || fieldValue === null || fieldValue === '') return null;
                          
                          return (
                            <div key={index} className="p-2 bg-gray-50 rounded border border-gray-200">
                              <div className="text-xs font-medium text-gray-700 mb-1">{field.label}</div>
                              {field.type === 'photo' && typeof fieldValue === 'string' ? (
                                <img
                                  src={fieldValue}
                                  alt={field.label}
                                  className="max-w-full h-auto rounded border border-gray-300 max-h-32 cursor-pointer"
                                  onClick={() => window.open(fieldValue, '_blank')}
                                />
                              ) : (
                                <div className="text-xs text-gray-900">
                                  {fieldValue === null || fieldValue === undefined 
                                    ? '—' 
                                    : typeof fieldValue === 'object' 
                                      ? '[Object]' 
                                      : String(fieldValue)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* AI Summary */}
                  {(selectedCompletion as FormCompletion).aiSummary && (
                    <div className="bg-purple-50 border border-purple-200 rounded p-2">
                      <h4 className="text-xs font-semibold text-gray-800 mb-1">AI Summary</h4>
                      <p className="text-xs text-gray-900">{(selectedCompletion as FormCompletion).aiSummary}</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">Unable to load completion details</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {showAddTaskPanel && asset?.locationId && assetId && (
        <AddTaskPanel
          onClose={() => setShowAddTaskPanel(false)}
          onTaskCreated={() => {
            setShowAddTaskPanel(false);
            loadAssetData();
          }}
          prepopulatedLocationId={asset.locationId}
          prepopulatedAssetIds={[assetId]}
        />
      )}

      {viewingTask && user?.id && (
        <CompleteTaskModal
          task={viewingTask as unknown as ComplianceTask}
          onComplete={async () => {
            const result = await completeTask(viewingTask.id, user.id);
            if (result.success) {
              showNotification('Evidence uploaded and task submitted for review', 'success');
            } else {
              showNotification(result.error || 'Failed to submit', 'error');
            }
            setViewingTask(null);
            loadAssetData();
          }}
          onClose={() => {
            loadTaskDraftEvidenceStatus(tasks, assetId || '');
            setViewingTask(null);
          }}
        />
      )}
    </div>
  );
};

export default AssetRecordPage;

